import React, { useEffect, useCallback, memo, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Key } from "uikit";

import { Dispatcher } from "../../../../../../../../services";
import { languageAction } from "../../../../../../../../redux/reducers/language";
import { sessionAction } from "../../../../../../../../redux/reducers/session";
import { accountAction } from "../../../../../../../../redux/reducers/account";
import { useTypedSelector } from "../../../../../../../../redux/store";
import {
	Language,
	languageOptions,
	languages,
} from "../../../../../../../../assets/languages/langs";
import { StyledSpan, Select2 } from "../../../../../../../common";
import { useAppContext } from "../../../../../../../../context";
import { storeLoginLanguage } from "../../../../../../../LoginLanguage";

const StyledSpanB = styled(StyledSpan)`
	color: rgba(0, 0, 0, 0.87);
	margin: 0;
	justify-content: center;
	text-align: center;
	font-weight: 700;
	font-size: 1.1rem;
	line-height: 1.3;
`;

const PopupContent: React.FC<LanguageSwitch.Props> = ({
	label = true,
	onClose,
}) => {
	const { t } = useTranslation();
	const { getTaxiServiceLanguages } = useAppContext();
	const dispatch = useDispatch();
	const defaultTaxiServiceId = useTypedSelector(
		(state) => state.session.defaultTaxiServiceId,
	);
	const language = useTypedSelector((state) => state.session.language);
	const user = useTypedSelector((state) => state.account?.user);

	const languageExclude = useMemo<Language[]>(() => {
		if (!defaultTaxiServiceId) return [];
		const existLanguages = getTaxiServiceLanguages(defaultTaxiServiceId);
		return languages.filter((lang) => !existLanguages.includes(lang));
	}, [defaultTaxiServiceId, getTaxiServiceLanguages]);

	const selectOptions = useMemo<LanguageSwitch.LanguageSelects>(
		() =>
			languageOptions.filter(
				(item) => !languageExclude.includes(item.value),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[languageOptions],
	);

	const onChangeSelect = useCallback(
		(value: LanguageSwitch.LanguageSelect) => {
			// The next login screen opens in the language used last.
			if (value?.value) storeLoginLanguage(value.value);
			dispatch(sessionAction.setLanguage(value.value));
			dispatch(languageAction.setLang(value.value));
			if (value?.value && user?.id) {
				Dispatcher.updateSettings(user.id, {
					...user.settings,
					language: value.value,
				});
				dispatch(accountAction.setLanguage(value.value));
			}

			onClose?.();
		},
		[dispatch, onClose, user],
	);

	useEffect(() => {
		if (!language) onChangeSelect(selectOptions[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [language, onChangeSelect, onClose, selectOptions]);

	return (
		<>
			{label && (
				<StyledSpanB>{t("main_header.language") || ""}</StyledSpanB>
			)}
			<Select2
				value={language}
				options={selectOptions}
				onSelect={onChangeSelect}
				w="200px"
			/>
		</>
	);
};

declare namespace LanguageSwitch {
	interface Props {
		label?: boolean;
		onClose?: () => void;
	}

	interface LanguageSelect {
		key: Key;
		label: string;
		value: Language;
	}

	type LanguageSelects = LanguageSelect[];
}
export const LanguageSwitchMemo = memo(PopupContent);
export default PopupContent;
